/**
 * Wetter: vereinheitlichtes Format für Open-Meteo (Vorhersage) und
 * GeoSphere Austria (offizielle Unwetterwarnungen), plus Luftfeuchte-Physik.
 *
 * Der Server holt die Daten und schickt sie in diesem Format an die App.
 * Die App rechnet daraus lokal die Tipps (siehe advice.ts).
 */

export interface WeatherNow {
  time: string;
  temp: number;
  rh: number;
  dewPoint: number;
  precip: number;
  code: number;
  cloud: number;
  wind: number;
  gusts: number;
  isDay: boolean;
  /** Globalstrahlung in W/m² */
  ghi: number;
  /** Direkte Sonnenstrahlung in W/m² (hoch = pralle Sonne) */
  dni: number;
  uv: number;
}

export interface WeatherHour {
  time: string;
  temp: number;
  rh: number;
  dewPoint: number;
  precipProb: number;
  precip: number;
  code: number;
  cloud: number;
  gusts: number;
  ghi: number;
  dni: number;
  uv: number;
  isDay: boolean;
  /** Gewitter-Energie (CAPE) in J/kg */
  cape: number;
}

export interface WeatherDay {
  date: string;
  code: number;
  tMax: number;
  tMin: number;
  sunrise: string;
  sunset: string;
  daylightH: number;
  sunshineH: number;
  uvMax: number;
  precipSum: number;
  precipProbMax: number;
  gustsMax: number;
  /** Tages-Sonnenenergie in MJ/m² */
  radiation: number;
}

export type WarningType = 'wind' | 'rain' | 'snow' | 'ice' | 'thunderstorm' | 'heat' | 'cold' | 'other';

export interface WeatherWarning {
  id: string;
  type: WarningType;
  /** 1 = gelb, 2 = orange, 3 = rot */
  level: number;
  start: string;
  end: string;
  text: string;
}

export interface WeatherData {
  fetchedAt: string;
  location: { name: string; lat: number; lon: number };
  now: WeatherNow;
  hours: WeatherHour[];
  days: WeatherDay[];
  warnings: WeatherWarning[];
  warningsFetchedAt: string | null;
  source: string;
}

// ---------------------------------------------------------------- Open-Meteo

export const OPEN_METEO_CURRENT = [
  'temperature_2m', 'relative_humidity_2m', 'dew_point_2m', 'precipitation', 'weather_code', 'cloud_cover',
  'wind_speed_10m', 'wind_gusts_10m', 'is_day', 'shortwave_radiation', 'direct_normal_irradiance', 'uv_index',
];
export const OPEN_METEO_HOURLY = [
  'temperature_2m', 'relative_humidity_2m', 'dew_point_2m', 'precipitation_probability', 'precipitation', 'weather_code',
  'cloud_cover', 'wind_gusts_10m', 'shortwave_radiation', 'direct_normal_irradiance', 'uv_index', 'is_day', 'cape',
];
export const OPEN_METEO_DAILY = [
  'weather_code', 'temperature_2m_max', 'temperature_2m_min', 'sunrise', 'sunset', 'daylight_duration', 'sunshine_duration',
  'uv_index_max', 'precipitation_sum', 'precipitation_probability_max', 'wind_gusts_10m_max', 'shortwave_radiation_sum',
];

export function openMeteoUrl(lat: number, lon: number, tz: string): string {
  const q = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
    current: OPEN_METEO_CURRENT.join(','),
    hourly: OPEN_METEO_HOURLY.join(','),
    daily: OPEN_METEO_DAILY.join(','),
    timezone: tz,
    timeformat: 'unixtime',
    forecast_days: '7',
    forecast_hours: '48',
    // Die letzten 24 h braucht die Luftfeuchte-Schätzung für drinnen
    past_hours: '24',
  });
  return `https://api.open-meteo.com/v1/forecast?${q}`;
}

type Json = Record<string, any>;

const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const iso = (unixSeconds: unknown): string => new Date(num(unixSeconds) * 1000).toISOString();

/** Open-Meteo-Antwort (timeformat=unixtime) ins App-Format bringen. */
export function parseOpenMeteo(json: Json, location: WeatherData['location'], fetchedAt: Date): Omit<WeatherData, 'warnings' | 'warningsFetchedAt'> {
  const c = json.current ?? {};
  const h = json.hourly ?? {};
  const d = json.daily ?? {};
  const utcOffset = num(json.utc_offset_seconds);
  const now: WeatherNow = {
    time: iso(c.time),
    temp: num(c.temperature_2m),
    rh: num(c.relative_humidity_2m),
    dewPoint: num(c.dew_point_2m),
    precip: num(c.precipitation),
    code: num(c.weather_code),
    cloud: num(c.cloud_cover),
    wind: num(c.wind_speed_10m),
    gusts: num(c.wind_gusts_10m),
    isDay: c.is_day === 1,
    ghi: num(c.shortwave_radiation),
    dni: num(c.direct_normal_irradiance),
    uv: num(c.uv_index),
  };
  const times: number[] = Array.isArray(h.time) ? h.time : [];
  const at = (key: string, i: number) => num(h[key]?.[i]);
  const hours: WeatherHour[] = times.map((t, i) => ({
    time: iso(t),
    temp: at('temperature_2m', i),
    rh: at('relative_humidity_2m', i),
    dewPoint: at('dew_point_2m', i),
    precipProb: at('precipitation_probability', i),
    precip: at('precipitation', i),
    code: at('weather_code', i),
    cloud: at('cloud_cover', i),
    gusts: at('wind_gusts_10m', i),
    ghi: at('shortwave_radiation', i),
    dni: at('direct_normal_irradiance', i),
    uv: at('uv_index', i),
    isDay: h.is_day?.[i] === 1,
    cape: at('cape', i),
  }));
  const dayTimes: number[] = Array.isArray(d.time) ? d.time : [];
  const dget = (key: string, i: number) => num(d[key]?.[i]);
  const days: WeatherDay[] = dayTimes.map((t, i) => ({
    // Tages-Zeitstempel sind lokale Mitternacht → mit dem UTC-Abstand zum Datum machen
    date: new Date((num(t) + utcOffset) * 1000).toISOString().slice(0, 10),
    code: dget('weather_code', i),
    tMax: dget('temperature_2m_max', i),
    tMin: dget('temperature_2m_min', i),
    sunrise: iso(d.sunrise?.[i]),
    sunset: iso(d.sunset?.[i]),
    daylightH: dget('daylight_duration', i) / 3600,
    sunshineH: dget('sunshine_duration', i) / 3600,
    uvMax: dget('uv_index_max', i),
    precipSum: dget('precipitation_sum', i),
    precipProbMax: dget('precipitation_probability_max', i),
    gustsMax: dget('wind_gusts_10m_max', i),
    radiation: dget('shortwave_radiation_sum', i),
  }));
  return { fetchedAt: fetchedAt.toISOString(), location, now, hours, days, source: 'Open-Meteo' };
}

// ------------------------------------------------------- GeoSphere Austria

export function geoSphereUrl(lat: number, lon: number): string {
  return `https://warnungen.zamg.at/wsapp/api/getWarningsForCoords?lon=${lon.toFixed(4)}&lat=${lat.toFixed(4)}&lang=de`;
}

const WARN_TYPES: Record<number, WarningType> = {
  1: 'wind',
  2: 'rain',
  3: 'snow',
  4: 'ice',
  5: 'thunderstorm',
  6: 'heat',
  7: 'cold',
};

/** Offizielle Warnungen (GeoSphere Austria) ins App-Format bringen. */
export function parseGeoSphere(json: Json): WeatherWarning[] {
  const list: Json[] = json?.properties?.warnings ?? [];
  return list
    .map((w) => {
      const p = w?.properties ?? {};
      const raw = p.rawinfo ?? {};
      const start = Number(raw.start);
      const end = Number(raw.end);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
      return {
        id: `${p.warnid ?? 'x'}-${p.chgid ?? 0}`,
        type: WARN_TYPES[Number(raw.wtype ?? p.warntypid)] ?? 'other',
        level: Number(raw.wlevel ?? p.warnstufeid ?? 1),
        start: new Date(start * 1000).toISOString(),
        end: new Date(end * 1000).toISOString(),
        text: String(p.text ?? '').trim(),
      } satisfies WeatherWarning;
    })
    .filter((w): w is WeatherWarning => w !== null)
    .sort((a, b) => a.start.localeCompare(b.start));
}

export const WARNING_LABEL: Record<WarningType, string> = {
  wind: 'Sturm',
  rain: 'Starkregen',
  snow: 'Schnee',
  ice: 'Glatteis',
  thunderstorm: 'Gewitter',
  heat: 'Hitze',
  cold: 'Kälte',
  other: 'Unwetter',
};

export const WARNING_LEVEL_LABEL = ['', 'gelb', 'orange', 'rot', 'violett'];

// ------------------------------------------------------------- Physik

/** Sättigungsdampfdruck in hPa (Magnus-Formel, über Wasser). */
export function saturationVaporPressure(tempC: number): number {
  return 6.112 * Math.exp((17.62 * tempC) / (243.12 + tempC));
}

/** Relative Feuchte in %, wenn Luft mit diesem Taupunkt auf `tempC` erwärmt/abgekühlt wird. */
export function rhAt(dewPointC: number, tempC: number): number {
  return Math.min(100, (100 * saturationVaporPressure(dewPointC)) / saturationVaporPressure(tempC));
}

/**
 * Wohnungen haben durch Atmen, Kochen, Duschen und Pflanzen etwas mehr Feuchte
 * als die Außenluft. Entspricht grob +2 °C Taupunkt.
 */
export const HOUSEHOLD_MOISTURE_K = 2;

/** Geschätzte Luftfeuchte drinnen aus dem Taupunkt draußen. */
export function estimateIndoorRh(outdoorDewPointC: number, indoorTempC: number): number {
  return Math.round(rhAt(outdoorDewPointC + HOUSEHOLD_MOISTURE_K, indoorTempC));
}

/**
 * Mittlerer Taupunkt draußen im Zeitraum [now − pastHours, now + futureHours].
 * Die Luft in der Wohnung „erinnert sich“ an die letzten Stunden – deshalb
 * zählt für drinnen vor allem die Vergangenheit.
 */
export function meanDewPoint(w: WeatherData, now: Date, pastHours = 18, futureHours = 2): number {
  const from = now.getTime() - pastHours * 3_600_000;
  const to = now.getTime() + futureHours * 3_600_000;
  const list = w.hours.filter((h) => {
    const t = Date.parse(h.time);
    return t >= from && t <= to;
  });
  if (!list.length) return w.now.dewPoint;
  return list.reduce((s, h) => s + h.dewPoint, 0) / list.length;
}

// ------------------------------------------------------------ Texte

export function weatherCodeLabel(code: number): { label: string; icon: string } {
  if (code === 0) return { label: 'Klar', icon: '☀️' };
  if (code === 1) return { label: 'Überwiegend klar', icon: '🌤️' };
  if (code === 2) return { label: 'Teils bewölkt', icon: '⛅' };
  if (code === 3) return { label: 'Bedeckt', icon: '☁️' };
  if (code === 45 || code === 48) return { label: 'Nebel', icon: '🌫️' };
  if (code >= 51 && code <= 57) return { label: 'Nieselregen', icon: '🌦️' };
  if (code >= 61 && code <= 67) return { label: code >= 65 ? 'Starker Regen' : 'Regen', icon: '🌧️' };
  if (code >= 71 && code <= 77) return { label: 'Schnee', icon: '🌨️' };
  if (code >= 80 && code <= 82) return { label: 'Regenschauer', icon: '🌦️' };
  if (code === 85 || code === 86) return { label: 'Schneeschauer', icon: '🌨️' };
  if (code >= 95) return { label: code >= 96 ? 'Gewitter mit Hagel' : 'Gewitter', icon: '⛈️' };
  return { label: 'Wetter', icon: '🌡️' };
}

export function isThunderCode(code: number): boolean {
  return code >= 95;
}

export function isRainCode(code: number): boolean {
  return (code >= 51 && code <= 67) || (code >= 80 && code <= 82) || code >= 95;
}

/** Stunden im Zeitraum [from, to). */
export function hoursBetween(w: WeatherData, from: Date, to: Date): WeatherHour[] {
  return w.hours.filter((h) => {
    const t = Date.parse(h.time);
    return t >= from.getTime() - 3_599_999 && t < to.getTime();
  });
}

/** Wetterstunde, in die dieser Zeitpunkt fällt. */
export function hourAt(w: WeatherData, at: Date): WeatherHour | undefined {
  const t = at.getTime();
  return w.hours.find((h) => {
    const s = Date.parse(h.time);
    return t >= s && t < s + 3_600_000;
  });
}

/** Aktive oder bald beginnende Warnungen. */
export function relevantWarnings(w: WeatherData, now: Date, withinHours = 6): WeatherWarning[] {
  const horizon = now.getTime() + withinHours * 3_600_000;
  return w.warnings.filter((x) => Date.parse(x.end) > now.getTime() && Date.parse(x.start) <= horizon);
}
